import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentTextIcon, EyeIcon, PencilIcon, EnvelopeIcon, LinkIcon, CalendarIcon, BanknotesIcon, StarIcon, PhoneIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';

const DocumentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  document, 
  collectionName, 
  mode = 'add' // 'add', 'edit', 'view'
}) => {
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && document && mode !== 'add') {
      // Populate form with document data
      Object.keys(document).forEach(key => {
        if (key !== 'id') {
          let value = document[key];
          // Handle Firestore timestamps
          if (value && typeof value === 'object' && value._seconds) {
            value = new Date(value._seconds * 1000).toISOString().slice(0, 16);
          }
          // Handle arrays and objects
          if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value, null, 2);
          }
          setValue(key, value);
        }
      });
    } else if (isOpen && mode === 'add') {
      if (reset && typeof reset === 'function') {
        reset();
      }
    }
  }, [isOpen, document, mode, setValue, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Process form data
      const processedData = { ...data };
      
      // Handle special fields based on collection
      Object.keys(processedData).forEach(key => {
        // Try to parse JSON strings back to objects
        if (typeof processedData[key] === 'string') {
          try {
            const parsed = JSON.parse(processedData[key]);
            processedData[key] = parsed;
          } catch (e) {
            // Keep as string if not valid JSON
          }
        }
        
        // Handle timestamps
        if (key.includes('date') || key.includes('time') || key.includes('At')) {
          if (processedData[key] && typeof processedData[key] === 'string') {
            const date = new Date(processedData[key]);
            // Only set if valid date
            if (!isNaN(date.getTime())) {
              processedData[key] = date;
            }
          }
        }
        
        // Handle numeric fields
        if ((key === 'amount' || key === 'rating' || key === 'price' || key === 'pricee' || key === 'students' || key === 'memberCount') && processedData[key]) {
          processedData[key] = Number(processedData[key]);
        }
      });

      await onSave(processedData);
      onClose();
      if (reset && typeof reset === 'function') {
        reset();
      }
    } catch (error) {
      console.error('Error saving document:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getFieldType = (key, value) => {
    if (key.includes('email')) return 'email';
    if (key.includes('url') || key.includes('link')) return 'url';
    if (key.includes('date') || key.includes('time') || key.includes('At')) return 'datetime-local';
    if (key === 'amount' || key === 'rating' || key === 'price' || key === 'pricee' || key === 'students' || key === 'memberCount' || key === 'count' || key === 'size' || key === 'usage_count' || key === 'value') return 'number';
    if (key.includes('telephone') || key.includes('phone')) return 'tel';
    if (key === 'description' || key === 'content' || key === 'review' || key === 'videos' || key === 'items') return 'textarea';
    if (typeof value === 'object' && value !== null) return 'textarea';
    if (typeof value === 'boolean' || key === 'active' || key === 'featured' || key === 'sent' || key === 'maintenance_mode') return 'checkbox';
    return 'text';
  };

  const getFieldIcon = (key, fieldType) => {
    if (key.includes('email')) return <EnvelopeIcon className="h-5 w-5" />;
    if (key.includes('url') || key.includes('link')) return <LinkIcon className="h-5 w-5" />;
    if (key.includes('date') || key.includes('time')) return <CalendarIcon className="h-5 w-5" />;
    if (key.includes('amount') || key.includes('price')) return <BanknotesIcon className="h-5 w-5" />;
    if (key.includes('rating')) return <StarIcon className="h-5 w-5" />;
    if (key.includes('phone') || key.includes('telephone')) return <PhoneIcon className="h-5 w-5" />;
    if (fieldType === 'textarea') return <DocumentTextIcon className="h-5 w-5" />;
    if (fieldType === 'checkbox') return <CheckIcon className="h-5 w-5" />;
    return <DocumentTextIcon className="h-5 w-5" />;
  };

  const renderField = (key, value) => {
    const fieldType = getFieldType(key, value);
    const isReadOnly = mode === 'view';
    const fieldIcon = getFieldIcon(key, fieldType);
    const fieldLabel = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

    if (key === 'id') return null;

    // Special rendering for view mode
    if (isReadOnly) {
      return (
        <div key={key} className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center mb-2">
            <div className="text-gray-500 mr-2">{fieldIcon}</div>
            <label className="text-sm font-semibold text-gray-700">
              {fieldLabel}
            </label>
          </div>
          <div className="pl-7">
            {fieldType === 'textarea' ? (
              <div className="bg-white p-3 rounded border text-sm text-gray-900 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : (value || '-')}
              </div>
            ) : fieldType === 'checkbox' ? (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {value ? 'Yes' : 'No'}
              </span>
            ) : (
              <div className="text-gray-900 font-medium">
                {value || <span className="text-gray-400 italic">Not set</span>}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (fieldType === 'textarea') {
      return (
        <div key={key} className="mb-6">
          <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
            <div className="text-gray-500 mr-2">{fieldIcon}</div>
            {fieldLabel}
          </label>
          <textarea
            {...register(key)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
          />
        </div>
      );
    }

    if (fieldType === 'checkbox') {
      return (
        <div key={key} className="mb-6 p-4 bg-gray-50 rounded-lg">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              {...register(key)}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2"
            />
            <span className="ml-3 flex items-center text-sm font-semibold text-gray-700">
              <div className="text-gray-500 mr-2">{fieldIcon}</div>
              {fieldLabel}
            </span>
          </label>
        </div>
      );
    }

    return (
      <div key={key} className="mb-6">
        <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
          <span className="text-lg mr-2">{fieldIcon}</span>
          {fieldLabel}
        </label>
        <input
          type={fieldType}
          {...register(key)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder={`Enter ${fieldLabel.toLowerCase()}...`}
          step={fieldType === 'number' ? 'any' : undefined}
        />
      </div>
    );
  };

  const getFieldsToRender = () => {
    if (document && Object.keys(document).length > 0) {
      return Object.keys(document).filter(key => key !== 'id' && key !== 'delivery');
    }
    
    // Default fields based on collection with better organization
    const defaultFields = {
      BooksReviews: ['bookId', 'userId', 'rating', 'review', 'createdAt'],
      FreeVideos: ['title', 'description', 'videos', 'category', 'createdAt'],
      blogs: ['title', 'description', 'content', 'link', 'userId', 'publishedAt'],
      books: ['title', 'description', 'amount', 'pdf_url', 'image_url', 'author', 'category'],
      carts: ['userId', 'title', 'items', 'total', 'createdAt'],
      categories: ['name', 'index', 'image_url', 'description', 'active'],
      classrooms: ['name', 'subject', 'category', 'description', 'createdBy', 'memberCount'],
      courses: ['name', 'pricee', 'price_status', 'image_url', 'cat_id', 'description', 'instructor'],
      formkey: ['key1', 'description', 'active', 'createdAt'],
      notifications: ['title', 'description', 'topic', 'type', 'sent', 'createdAt'],
      pdfs: ['name', 'url', 'size', 'category', 'uploadedAt'],
      reviews: ['course_id', 'review', 'rating', 'userId', 'createdAt'],
      settings: ['license', 'featured', 'email', 'maintenance_mode', 'app_version'],
      tags: ['name', 'color', 'category', 'usage_count'],
      user_stats: ['userId', 'count', 'type', 'date', 'value'],
      users: ['name', 'email', 'telephone', 'role', 'active', 'createdAt', 'lastLogin'],
      withdrawals: ['userId', 'amount', 'method', 'paymentaddress', 'status', 'requestedAt', 'processedAt']
    };

    return defaultFields[collectionName] || ['title', 'description'];
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {mode === 'view' ? (
                  <EyeIcon className="h-6 w-6 text-white mr-3" />
                ) : mode === 'edit' ? (
                  <PencilIcon className="h-6 w-6 text-white mr-3" />
                ) : (
                  <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
                )}
                <h3 className="text-xl font-semibold text-white capitalize">
                  {mode === 'add' ? 'Add New' : mode === 'edit' ? 'Edit' : 'View'} {collectionName} Document
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {document?.id && (
              <div className="mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white">
                  ID: {document.id}
                </span>
              </div>
            )}
          </div>
          
          <div className="bg-white px-6 py-6">
            <form onSubmit={handleSubmit(onSubmit)} className="max-h-[60vh] overflow-y-auto pr-2">
              {getFieldsToRender().map(key => renderField(key, document?.[key]))}
            </form>
          </div>

          <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t">
            {mode !== 'view' ? (
              <>
                <button
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;
